/* eslint-disable @typescript-eslint/no-explicit-any */
import { schema } from 'normalizr';

import { compact, date } from '../../../../helpers/compact';
import { LINK_TYPE_DOC } from '../../../../types';
import { Document, DocumentType } from '../../../../types/document';
import { toDocType } from '../../mapping';

export type DocumentLink =
  | Document
  | {
      linkType: typeof LINK_TYPE_DOC;
      type: string;
    };

const getDocumentLink = (doc: any): DocumentLink =>
  compact({
    ...doc,
    linkType: LINK_TYPE_DOC,
    type: toDocType(doc?.type),
  });

const processDocument = <T extends Document>(doc: any): T =>
  compact({
    id: doc.id,
    uid: doc.uid,
    type: toDocType(doc.type),
    lang: doc.lang,
    alternateLanguages: doc.alternate_languages?.map(getDocumentLink),
    createdAt: date(
      doc.data?.publication_date || doc.first_publication_date,
      'YYYY-MM-DDTHH:mm:ssZ'
    ),
    updatedAt: date(doc.last_publication_date, 'YYYY-MM-DDTHH:mm:ssZ'),
    disableRgpbBanner:
      doc.uid === 'terms-of-use' ||
      doc.uid === 'conditions-generales-d-utilisation',
  });

/**
 * Function to transform a prismic custom types to a document.
 *
 * @param type Type of document
 * @param opts Normalizr options
 * @returns the normalized document
 */
const documentEntity = <T extends Document = Document>(
  type: DocumentType,
  { processStrategy, ...rest }: schema.EntityOptions<T>
): schema.Entity<T> =>
  new schema.Entity<T>(
    type,
    {},
    {
      idAttribute: 'id',
      processStrategy: (entity, parent, key): T => {
        if (!entity) {
          return undefined;
        }

        return processStrategy !== undefined
          ? {
              ...processDocument(entity),
              ...processStrategy(entity, parent, key),
            }
          : processDocument(entity);
      },
      ...rest,
    }
  );

export default documentEntity;
